import type { IParagraph } from '@src/slide/domain/ui-model/element-modeles/property/text/interface/editor-para.interface';
import type { ITextEditor } from '@src/slide/domain/ui-model/element-modeles/property/text/interface/text-editor.interface';
import type { IElementBaseExtensions, IElementBaseModel } from './base/element-base-model.interface';
import type { IShapeModelProps } from '@src/slide/domain/ui-model/element-modeles/Interface/model-props/shape-model-props.interface';
import type {
    IGraphicProperty,
} from '@src/slide/domain/ui-model/element-modeles/property/graphic-info/graphic-info.interface';
import type { AutoFitType } from '@src/slide/domain/ui-model/element-modeles/property/text/constants/text-style-type';
import type { IImageFillEffect } from '../property/fill/image-fill/effect/image-fill-effect.interface';
import type { IImageFillSize } from '../property/fill/image-fill/size/image-fill-size.interface';
import type { IImageCrop } from '../property/image-operation/interface/image-crop.interface';
import type { ITextBodyStyle } from '../property/text/interface/editor-styles.interface';
import { AddonMode } from '../property/addon/addon.interface';
import { NonEmptyString } from '@src/slide/infrastructure/module/independent/types';
import { ICTShapeStyle, ICTTableCell, IShapeDataModel } from '@tencent/slide-sdk-model';

export interface IShapeBaseModel extends IElementBaseModel {
    sdkData: IShapeDataModel | null;
    props: IShapeModelProps;
    paragraphList?: IParagraph[];
    textBodyStyle?: any;
    updateParagraphList(paragraphList, textBodyStyle?): void;
    prepareSDKParagraphListData(paragraphList, textBodyStyle): any;
    /**
     * 更新 textBodyStyle
     */
    updateTextBodyStyle(textBodyStyle): void;
    updateMasterAndLayoutPlaceHolder(paragraphList, textBodyStyle, isTextCustom);
}

export interface IShapeModel extends IShapeBaseModel {
    isText: boolean;
    inTable: boolean;
    textBodyStyle: ITextBodyStyle;
    editorInstance: ITextEditor | null;
    isEditing: boolean;
    selectionStyles: any;
    isAttachmentShown: boolean;
    editorEnterFocus?: boolean;
    hasSlideNum: boolean;
    undoRedo: boolean;
    subType?: number;
    initFocus?: boolean;
    /**
     * 是否为公式
     */
    isTextMath: boolean;
    readonly isTextOrPlaceholder: boolean;
    cellInfo?: ICTTableCell;
    extensions: IShapeExtensions;
    isAttachmentUploaded(): boolean;
    /**
     * 图形信息接口
     */
    getGraphic(): IGraphicProperty;
    /**
     * 更新文字
     * @param textAdjust
     */
    updateTextAdjust(textAdjust: Partial<ITextAdjust>): void;
    /**
     * 更新是否为保持平面展示文字（3d相关功能）
     * @param isFlatText
     */
    updateShowFlatText(isFlatText: boolean): void;
    /**
     * 更新内边距
     * @param updateStyle
     */
    updatePaddingAdjust(updateStyle: Partial<ITextPaddingAdjust>): void;
    /**
     * 存在幻灯片编号
     */
    hasSlideNumber(): boolean;
    /**
     * 是否是文本形式的附件
     */
    isTextAttachment(): boolean;
    /**
     * 是否支持设置箭头样式
     */
    canSetArrowStyle(): boolean;
    /**
     * 图片填充效果相关
     */
    getImageFillEffect(): IImageFillEffect | undefined;
    /**
     * 图片填充大小相关
     */
    getImageFillSize(): IImageFillSize;
    /**
     * 图片填充图片裁剪相关
     */
    getImageCrop(): IImageCrop;
    /**
     * 只更新UImodel上的paagraphList
     */
    updateParagraphListForUI(paragraphList): void;

    setEditorComponent(editorComponent: ITextEditor | null): void;

    setUpdateState(updateState): void;

    getTextContent(): string;
}

export type IIframeShapeModel = IShapeModel & {
    props: {
        addon: {
            displayMode: typeof AddonMode.IFRAME;
        }
    }
};

export type IIframeTextModeShapeModel = IShapeModel & {
    props: {
        addon: {
            displayMode: typeof AddonMode.NORMAL;
            iframeId: NonEmptyString;
        }
    }
};

export type IAttachmentShapeModel = IShapeModel & {
    props: {
        // TODO: attachment 需要类型约束
        attachment: NonNullable<any>;
    }
};

export interface ITextAdjust {
    autoFitType?: AutoFitType;
    autoBreakType?: 'pre' | 'pre-wrap';
}

/**
 * TODO 这里使用继承看起来方法实现有点问题，是之前为了解bug加的逻辑，需要重新审视是否合理
 */
export interface ITextPaddingAdjust extends ITextAdjust {
    paddingLeft?: number;
    paddingRight?: number;
    paddingTop?: number;
    paddingBottom?: number;
}

export const ShapeSubType = {
    SHAPE: 0,
    TEXT_ELEMENT: 1,  // 形状是文本框
};

/**
 * 普通图形上的扩展字段，目前没有所以是空
 */
export interface IShapeExtensions extends IElementBaseExtensions {}
